import request from '@/config/axios'
import type { RegisterVO, RegisterRespVO, UserLoginVO, TokenType } from './types'

// ========== 开发环境 Mock（后端未启动时使用，仅本地生效）==========
// 打包 test/prd 时 import.meta.env.DEV 为 false，以下 mock 全部失效
// 注意：login / 注册 / 上传 已走真实接口，这里仅 getInfo / 字典 / 登出 仍用 mock
const mockAuth = import.meta.env.DEV && import.meta.env.VITE_MOCK_AUTH === 'true'

// 【已停用】菜单兜底：后端已在 system_menu 配好菜单并给角色授权，改用后端菜单。
// 保留 mockMenu / mockUserInfo.menus 仅为应急回滚，确认稳定后可整块删除，
// 同时删掉 .env.test 的 VITE_MOCK_MENU。
// const mockMenu = mockAuth || import.meta.env.VITE_MOCK_MENU === 'true'

// 假用户权限信息（备用：getInfo 真实接口不可用时，可临时改回 return Promise.resolve(mockUserInfo)）
export const mockUserInfo = {
  permissions: ['*:*:*'],
  roles: ['super_admin'],
  user: { id: 1, avatar: '', nickname: '测试用户', deptId: 1 },
  // 菜单：结构与后端返回一致，component 会被 generateRoute 用来匹配 views/ 下的 .vue 文件
  // 每个顶级菜单 = 目录 + 单个同名子菜单 + alwaysShow:false，会被折叠成一个平级可点项
  menus: [
    {
      name: '待接订单',
      path: '/order',
      component: '',
      icon: 'ep:document',
      visible: true,
      parentId: 0,
      alwaysShow: false,
      children: [
        {
          name: '待接订单',
          // path 与后端 system_menu 保持一致（线上是 /order/orderList，不是 /order/list），
          // 否则首页待办跳转在 mock 兜底生效时会 404
          path: '/order/orderList',
          component: 'Order/OrderList',
          icon: 'ep:document',
          visible: true,
          keepAlive: true
        }
      ]
    },
    {
      name: '已拒订单',
      path: '/rejected',
      component: '',
      icon: 'ep:circle-close',
      visible: true,
      parentId: 0,
      alwaysShow: false,
      children: [
        {
          name: '已拒订单',
          path: '/rejected/list',
          component: 'Order/RejectedList',
          icon: 'ep:circle-close',
          visible: true,
          keepAlive: true
        }
      ]
    },
    {
      name: '接单处理',
      path: '/approval',
      component: '',
      icon: 'ep:finished',
      visible: true,
      parentId: 0,
      alwaysShow: false,
      children: [
        {
          name: '接单处理',
          path: '/approval/list',
          component: 'Order/ApprovalList',
          icon: 'ep:finished',
          visible: true,
          keepAlive: true
        }
      ]
    },
    {
      name: '回执结单',
      path: '/receipt',
      component: '',
      icon: 'ep:money',
      visible: true,
      parentId: 0,
      alwaysShow: false,
      children: [
        {
          name: '回执结单',
          path: '/receipt/list',
          component: 'Order/ReceiptList',
          icon: 'ep:money',
          visible: true,
          keepAlive: true
        }
      ]
    },
    {
      name: '机构信息',
      path: '/agency',
      component: '',
      icon: 'ep:office-building',
      visible: true,
      parentId: 0,
      alwaysShow: false,
      children: [
        {
          name: '机构信息',
          path: '/agency/info',
          component: 'Agency/AgencyInfo',
          icon: 'ep:office-building',
          visible: true,
          keepAlive: true
        }
      ]
    },
    {
      name: '价格设置',
      path: '/price',
      component: '',
      icon: 'ep:price-tag',
      visible: true,
      parentId: 0,
      alwaysShow: false,
      children: [
        {
          name: '价格设置',
          path: '/price/setting',
          component: 'Price/PriceSetting',
          icon: 'ep:price-tag',
          visible: true,
          keepAlive: true
        }
      ]
    }
  ]
}

// 登录（真实接口）
// skipErrorNotify：让「账号密码不正确」等业务错误不弹全局通知卡片，
// 由 Login.vue 用 ElMessage 轻提示，交互更贴合登录场景
export const login = (data: UserLoginVO) => {
  return request.post<TokenType>({
    url: '/system/bidding/login',
    data,
    skipErrorNotify: true,
    headers: {
      isEncrypt: false
    }
  })
}

// 竞价机构注册
// skipErrorNotify：「登录账号已存在」等业务错误由 RegisterForm.vue 轻提示，不弹全局通知卡片
export const register = (data: RegisterVO) => {
  return request.post<RegisterRespVO>({
    url: '/system/bidding/register-tenant',
    data,
    skipErrorNotify: true
  })
}

// 登出
export const loginOut = () => {
  if (mockAuth) return Promise.resolve(true)
  return request.post({ url: '/system/auth/logout' })
}

// 获取用户权限信息（含 permissions / roles / menus）
// 真实调用；若后端返回 null（接口未就绪 / 无数据），回退到 mock，保证能进系统
export const getInfo = async () => {
  const res = await request.get({ url: '/system/auth/get-permission-info' })
  // 后端完全没返回：mock 环境下整体回退 mock，否则返回 null
  if (!res) {
    if (mockAuth) return mockUserInfo
    return null
  }
  // 【已停用】菜单兜底：后端菜单已配好，直接用后端返回的 menus。
  // 若后端菜单出问题需应急回滚，解开这里与顶部 mockMenu 的注释即可。
  // if (mockMenu && (!res.menus || res.menus.length === 0)) {
  //   res.menus = mockUserInfo.menus
  // }
  return res
}
